import { Outlet } from 'react-router-dom';

import { InlineRouteErrorPage } from '../../pages/InlineRouteErrorPage';
import { crudChildren } from './helpers';
import { lazyRoute } from './lazyRoute';
import { PlatformGuard } from '../guards/PlatformGuard';

/**
 * One element, reused by every admin section below — a route element is a
 * descriptor, and at most one of these can be on screen at a time.
 *
 * The nearest `errorElement` above these mounts belongs to `/`, whose element
 * IS the application shell, so a chunk that fails to load under `/admin/tenants`
 * used to unmount `AppProviders`, the sidebar, the header and every socket.
 * Stopping at the section leaves all of that up and the admin nav usable.
 */
const errorElement = <InlineRouteErrorPage />;

export const adminRoutes = [
  {
    children: crudChildren(
      '/Admin/PrivacyPoliciesPages/PrivacyPoliciesPage',
      '/Admin/PrivacyPoliciesPages/PrivacyPolicyPage',
    ),
    errorElement,
    handle: { title: 'privacyPolicies' },
    path: 'privacy-policies',
  },
  {
    children: crudChildren('/RolesPages/RolesPage', '/RolesPages/RolePage'),
    errorElement,
    handle: { title: 'roles' },
    path: 'roles',
  },
  {
    children: crudChildren(
      '/Admin/TenantPages/TenantListPage',
      '/Admin/TenantPages/TenantPage',
    ),
    errorElement,
    handle: { title: 'tenants' },
    path: 'tenants',
  },
  {
    children: crudChildren(
      '/Admin/Organizations/OrganizationsListPage',
      '/Admin/Organizations/OrganizationPage',
    ),
    errorElement,
    handle: { title: 'organizations' },
    path: 'organizations',
  },
  {
    children: [
      ...crudChildren(
        '/Admin/CorpOrganizations/CorpOrganizationsListPage',
        '/Admin/CorpOrganizations/CorpOrganizationPage',
      ),
      // MV2-527 — overall (no id) and per-company (:id) corpo stats share
      // one lazy-loaded page; static 'stats' segment wins over ':id' for
      // the literal '/corp-organizations/stats' URL (React Router ranks a
      // static segment above a dynamic one regardless of array order).
      {
        handle: { title: 'corpoStats' },
        lazy: () => lazyRoute('/Admin/CorpOrganizations/CorpoStatsPage'),
        path: 'stats',
      },
      {
        handle: { title: 'corpoStats' },
        lazy: () => lazyRoute('/Admin/CorpOrganizations/CorpoStatsPage'),
        path: ':id/stats',
      },
    ],
    errorElement,
    handle: { title: 'corpOrganizations' },
    path: 'corp-organizations',
  },
  {
    children: [
      { index: true, lazy: () => lazyRoute('/Categories/CategoryPage') },
      {
        children: [
          {
            children: [
              {
                handle: { title: 'add' },
                lazy: () => lazyRoute('/Categories/CategoryPage'),
                path: 'new',
              },
              {
                handle: { title: ':tagTitle' },
                lazy: () => lazyRoute('/Categories/CategoryPage'),
                path: ':id',
              },
            ],
            // No page of its own: the crumb (and the tag editor's Back
            // arrow) leads to the category that lists these tags.
            handle: { title: 'tags', breadcrumbTo: '..' },
            path: 'tags',
          },
          { index: true, lazy: () => lazyRoute('/Categories/CategoryPage') },
        ],
        handle: { title: ':categoryTitle' },
        path: ':categoryId',
      },
    ],
    element: (
      <PlatformGuard>
        <Outlet />
      </PlatformGuard>
    ),
    errorElement,
    handle: { title: 'categories' },
    path: 'categories',
  },
  {
    children: [
      { index: true, lazy: () => lazyRoute('/FinancePages/AdminFinancePage') },
    ],
    errorElement,
    handle: { title: 'finance' },
    path: 'finance',
  },
  {
    children: [
      { index: true, lazy: () => lazyRoute('/AdminStatsPages/AdminStatsPage') },
    ],
    errorElement,
    handle: { title: 'stats' },
    path: 'stats',
  },
  {
    children: [{ index: true, lazy: () => lazyRoute('/Admin/RebrandPage') }],
    errorElement,
    handle: { title: 'rebrand' },
    path: 'rebrand',
  },
  {
    children: [
      {
        index: true,
        lazy: () => lazyRoute('/Admin/PublicPages/PublicPagesAdminPage'),
      },
    ],
    errorElement,
    handle: { title: 'publicPages' },
    path: 'public-pages',
  },
  {
    children: [
      {
        index: true,
        lazy: () =>
          lazyRoute('/Admin/MarketplaceAccess/MarketplaceAccessAdminPage'),
      },
    ],
    errorElement,
    handle: { title: 'marketplaceAccess' },
    path: 'marketplace-access',
  },
];
